import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../components/ui/select";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { ShieldCheck, CheckCircle2 } from "lucide-react";
import Seo from "../components/Seo";

export default function LeadFormPage() {
    const location = useLocation();
    const navigate = useNavigate();
    const prefillPackage = location.state?.package || "Silber";
    const caseId = location.state?.case_id;

    const [form, setForm] = useState({
        first_name: "",
        last_name: "",
        email: "",
        phone: "",
        relation: "",
        care_grade: "",
        nursing_home_status: "",
        income_band: "",
        package_interest: prefillPackage,
        message: "",
        consent_privacy: false,
        consent_contact: false,
    });
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);

    const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.consent_privacy) {
            toast.error("Bitte Datenschutz-Einwilligung bestätigen.");
            return;
        }
        setLoading(true);
        try {
            await api.post("/leads", { ...form, case_id: caseId });
            setDone(true);
            toast.success("Ihre Anfrage wurde übermittelt. Wir melden uns zeitnah.");
        } catch (err) {
            toast.error(formatApiError(err, "Leider ist ein Fehler aufgetreten."));
        } finally {
            setLoading(false);
        }
    };

    if (done) {
        return (
            <div className="min-h-[70vh] bg-brand-50/30 py-16">
                <div className="container max-w-lg text-center">
                    <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-brand-500 text-white shadow-soft">
                        <CheckCircle2 className="h-8 w-8" />
                    </div>
                    <h1 className="mt-5 font-display text-3xl font-bold">Vielen Dank.</h1>
                    <p className="mt-2 text-ink-500">
                        Ihre Anfrage ist bei uns eingegangen. Wir melden uns diskret – in der Regel innerhalb
                        eines Werktages.
                    </p>
                    <Button
                        className="mt-6 h-12 rounded-full bg-brand-500 hover:bg-brand-600"
                        onClick={() => navigate("/konto")}
                        data-testid="lead-done-to-account"
                    >
                        Zu meinem Konto
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-[80vh] bg-brand-50/30 py-10">
            <Seo
                title="Persönliche Analyse anfragen"
                description="Unverbindliche, kostenlose Anfrage für eine persönliche Analyse Ihrer Situation rund um Pflegeheimkosten und Elternunterhalt. Diskret und DSGVO-konform."
                path="/kontakt"
            />
            <div className="container max-w-2xl">
                <div className="rounded-3xl border border-ink-300/30 bg-white p-6 shadow-softer sm:p-8">
                    <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-brand-600">
                        <ShieldCheck className="h-4 w-4" /> Vertrauliche Anfrage
                    </div>
                    <h1 className="mt-2 font-display text-3xl font-bold">Persönliche Analyse anfragen</h1>
                    <p className="mt-2 text-sm text-ink-500">
                        Unverbindlich. Kostenlos. Keine automatisierte Rechtsberatung. Wir nehmen diskret
                        Kontakt zu Ihnen auf.
                    </p>

                    <form onSubmit={submit} className="mt-6 space-y-5">
                        <div className="grid gap-4 sm:grid-cols-2">
                            <div>
                                <Label htmlFor="fn">Vorname</Label>
                                <Input id="fn" required value={form.first_name} onChange={(e) => setField("first_name", e.target.value)} className="mt-1 h-12 rounded-xl" data-testid="lead-first-name" />
                            </div>
                            <div>
                                <Label htmlFor="ln">Nachname</Label>
                                <Input id="ln" required value={form.last_name} onChange={(e) => setField("last_name", e.target.value)} className="mt-1 h-12 rounded-xl" data-testid="lead-last-name" />
                            </div>
                            <div>
                                <Label htmlFor="em">E-Mail</Label>
                                <Input id="em" type="email" required value={form.email} onChange={(e) => setField("email", e.target.value)} className="mt-1 h-12 rounded-xl" data-testid="lead-email" />
                            </div>
                            <div>
                                <Label htmlFor="ph">Telefon</Label>
                                <Input id="ph" type="tel" required value={form.phone} onChange={(e) => setField("phone", e.target.value)} className="mt-1 h-12 rounded-xl" data-testid="lead-phone" />
                            </div>
                        </div>

                        <div className="grid gap-4 sm:grid-cols-2">
                            <div>
                                <Label>Rolle</Label>
                                <Select value={form.relation} onValueChange={(v) => setField("relation", v)}>
                                    <SelectTrigger className="mt-1 h-12 rounded-xl" data-testid="lead-relation"><SelectValue placeholder="Ihre Rolle" /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="son">Sohn</SelectItem>
                                        <SelectItem value="daughter">Tochter</SelectItem>
                                        <SelectItem value="self">Selbst betroffen</SelectItem>
                                        <SelectItem value="partner">Ehe-/Lebenspartner</SelectItem>
                                        <SelectItem value="other">Anderes Familienmitglied</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>Pflegegrad</Label>
                                <Select value={form.care_grade} onValueChange={(v) => setField("care_grade", v)}>
                                    <SelectTrigger className="mt-1 h-12 rounded-xl" data-testid="lead-care-grade"><SelectValue placeholder="z. B. PG 3" /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="none">Kein Pflegegrad</SelectItem>
                                        <SelectItem value="pg1">Pflegegrad 1</SelectItem>
                                        <SelectItem value="pg2">Pflegegrad 2</SelectItem>
                                        <SelectItem value="pg3">Pflegegrad 3</SelectItem>
                                        <SelectItem value="pg4">Pflegegrad 4</SelectItem>
                                        <SelectItem value="pg5">Pflegegrad 5</SelectItem>
                                        <SelectItem value="unknown">Unbekannt</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>Heim-Status</Label>
                                <Select value={form.nursing_home_status} onValueChange={(v) => setField("nursing_home_status", v)}>
                                    <SelectTrigger className="mt-1 h-12 rounded-xl" data-testid="lead-home-status"><SelectValue placeholder="bitte wählen" /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="yes">Bereits im Heim</SelectItem>
                                        <SelectItem value="planned">In Planung</SelectItem>
                                        <SelectItem value="no">Nein</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>Einkommenslage</Label>
                                <Select value={form.income_band} onValueChange={(v) => setField("income_band", v)}>
                                    <SelectTrigger className="mt-1 h-12 rounded-xl" data-testid="lead-income"><SelectValue placeholder="grobe Einordnung" /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="under_100k">Unter 100.000 €</SelectItem>
                                        <SelectItem value="over_100k">Über 100.000 €</SelectItem>
                                        <SelectItem value="unsure">Unsicher</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="sm:col-span-2">
                                <Label>Gewünschtes Paket</Label>
                                <Select value={form.package_interest} onValueChange={(v) => setField("package_interest", v)}>
                                    <SelectTrigger className="mt-1 h-12 rounded-xl" data-testid="lead-package"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="Bronze">Bronze – Erste Analyse</SelectItem>
                                        <SelectItem value="Silber">Silber – Detaillierte Prüfung</SelectItem>
                                        <SelectItem value="Gold">Gold – Strategische Begleitung</SelectItem>
                                        <SelectItem value="Unsicher">Noch unsicher</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        <div>
                            <Label htmlFor="msg">Nachricht (optional)</Label>
                            <Textarea
                                id="msg"
                                value={form.message}
                                onChange={(e) => setField("message", e.target.value)}
                                className="mt-1 min-h-[100px] rounded-xl"
                                placeholder="Kurz zur Situation: worum geht es?"
                                data-testid="lead-message"
                            />
                        </div>

                        <div className="space-y-3 rounded-xl bg-brand-50/50 p-4 text-sm text-ink-700">
                            <label className="flex items-start gap-3">
                                <Checkbox checked={form.consent_privacy} onCheckedChange={(v) => setField("consent_privacy", !!v)} className="mt-1" data-testid="lead-consent-privacy" />
                                <span>
                                    Ich habe die Datenschutzerklärung gelesen und stimme der Verarbeitung meiner
                                    Angaben zur Kontaktaufnahme zu.
                                </span>
                            </label>
                            <label className="flex items-start gap-3">
                                <Checkbox checked={form.consent_contact} onCheckedChange={(v) => setField("consent_contact", !!v)} className="mt-1" data-testid="lead-consent-contact" />
                                <span>Ich möchte zur Analyse telefonisch oder per E-Mail kontaktiert werden.</span>
                            </label>
                        </div>

                        <Button type="submit" disabled={loading} className="h-14 w-full rounded-full bg-brand-500 text-base font-semibold hover:bg-brand-600" data-testid="lead-submit">
                            {loading ? "Wird gesendet…" : "Unverbindliche Anfrage senden"}
                        </Button>
                    </form>
                </div>
            </div>
        </div>
    );
}
